import React, { useState } from 'react';
import styled from 'styled-components';
import SolanaNavBar from '../components/solana/SolanaNavBar';
import { SolanaTokenList } from '../components/solana/SolanaTokenList';
import CreateSolanaTokenModal from '../components/solana/CreateSolanaTokenModal';
import SolanaPriceFooter from '../components/solana/SolanaPriceFooter';
import { Plus } from 'lucide-react';

// Solana Brand Colors
const SOL_PURPLE = '#9945FF';
const SOL_GREEN = '#14F195';
const SOL_DARK = '#333333';
const SOL_LIGHT = '#FFFFFF';
const SOL_BLACK = '#000000';

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${SOL_BLACK};
  color: ${SOL_LIGHT};
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: 60px; /* Space for footer */
`;

const HeroSection = styled.div`
  padding: 60px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: linear-gradient(180deg, rgba(153, 69, 255, 0.05) 0%, rgba(0, 0, 0, 0) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 800;
  color: ${SOL_LIGHT};
  margin: 0 0 16px 0;
  letter-spacing: -1px;
  
  span {
    background: linear-gradient(135deg, ${SOL_PURPLE} 0%, ${SOL_GREEN} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 0 32px 0;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const HeroSubtitleFirst = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 0 8px 0;
  line-height: 1.6;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, ${SOL_PURPLE} 0%, ${SOL_GREEN} 100%);
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(153, 69, 255, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(153, 69, 255, 0.4);
  }
`;

const FAQSection = styled.div`
  max-width: 700px;
  margin: 0 0 32px 0;
  text-align: left;
`;

const FAQTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${SOL_LIGHT};
  margin: 0 0 16px 0;
  text-align: center;
`;

const FAQItem = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.2s;
  
  &:hover {
    border-color: ${SOL_PURPLE};
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Question = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  padding: 14px 18px;
  text-align: left;
  font-size: 15px;
  font-weight: 600;
  color: ${SOL_LIGHT};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  
  &:hover {
    color: ${SOL_PURPLE};
  }
  
  span {
    transition: transform 0.2s;
  }
`;

const Answer = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  
  p {
    padding: 0 18px 14px 18px;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const SolanaPageMinimal: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Force black background for Solana page
  React.useEffect(() => {
    const originalBackground = document.body.style.background;
    const originalColor = document.body.style.color;

    document.body.style.background = '#000000';
    document.body.style.color = '#FFFFFF';

    return () => {
      document.body.style.background = originalBackground;
      document.body.style.color = originalColor;
    };
  }, []);

  const handleCreateSuccess = () => {
    window.location.reload();
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "How is LP locked?",
      answer: "All LP's are sent to the Protocol's LP Custodial Wallet. A custom locker program is being developed for creator revenue sharing."
    },
    {
      question: "How much do creators earn?",
      answer: "Creator earnings are TBD at this time, pending the development of the locker program."
    },
    {
      question: "Do token contracts need to be renounced?",
      answer: "Yes, every new token created automatically has its mint authority and freeze authority revoked at launch, making the token fully decentralized."
    },
    {
      question: "How does the token have liquidity at launch?",
      answer: "Using Mavrk's Market Formation methodology, liquidity is bootstrapped onchain with every trade, as the token immediately trades on DEX (Orca). No pre-sale mechanics, no bonding curves, no gatekeeping."
    }
  ];

  return (
    <PageContainer>
      <SolanaNavBar />

      <MainContent>
        <HeroSection>
          <HeroTitle>
            <span>Solana</span> Token Factory
          </HeroTitle>
          <HeroSubtitleFirst>
            Make SOL Memes Great Again.
          </HeroSubtitleFirst>
          <HeroSubtitle>
            Deploy tokens on the world's fastest programmable blockchain.
          </HeroSubtitle>

          <FAQSection>
            <FAQTitle>Frequently Asked Questions</FAQTitle>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <Question onClick={() => toggleFAQ(index)}>
                  {faq.question}
                  <span style={{ transform: openFAQ === index ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </Question>
                <Answer $isOpen={openFAQ === index}>
                  <p>{faq.answer}</p>
                </Answer>
              </FAQItem>
            ))}
          </FAQSection>

          <ActionButton onClick={() => setIsCreateModalOpen(true)}>
            <Plus size={20} />
            Deploy Token
          </ActionButton>
        </HeroSection>

        <SolanaTokenList searchQuery={searchQuery} onSearchChange={setSearchQuery} />
      </MainContent>

      <CreateSolanaTokenModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <SolanaPriceFooter />
    </PageContainer>
  );
};

export default SolanaPageMinimal;

